import React from 'react';
import { useParams } from 'react-router';
import {
  PageHeader,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Input,
  Label,
  LoadingSkeleton,
} from '@redbax/ui-kit';
import { Plus, Trash2, CheckCircle2, XCircle } from 'lucide-react';
import {
  useAdminPackageRegistry,
  useUpdateAdminPackageRegistry,
  useAddAdminPackageRegistryDomain,
  useRemoveAdminPackageRegistryDomain,
  useToggleAdminPackageRegistry,
} from '../api/admin-package-hooks';

export function AdminPackageRegistryDetailPage() {
  const { registryId } = useParams<{ registryId: string }>();
  const { data: registry, isLoading } = useAdminPackageRegistry(registryId ?? '');
  const updateRegistry = useUpdateAdminPackageRegistry();
  const addDomain = useAddAdminPackageRegistryDomain();
  const removeDomain = useRemoveAdminPackageRegistryDomain();
  const toggleRegistry = useToggleAdminPackageRegistry();

  const [editName, setEditName] = React.useState('');
  const [newDomain, setNewDomain] = React.useState('');
  const [isEditing, setIsEditing] = React.useState(false);

  React.useEffect(() => {
    if (registry) {
      setEditName(registry.name);
    }
  }, [registry]);

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Package Registry" description="Loading..." />
        <LoadingSkeleton rows={6} />
      </div>
    );
  }

  if (!registry) {
    return (
      <div>
        <PageHeader title="Package Registry" description="Registry not found" />
        <div className="flex h-32 items-center justify-center text-sm text-text-secondary">
          Registry not found
        </div>
      </div>
    );
  }

  const handleSaveName = () => {
    if (!editName.trim() || !registryId) return;
    updateRegistry.mutate({ id: registryId, name: editName.trim(), isEnabled: registry.isEnabled });
    setIsEditing(false);
  };

  const handleAddDomain = () => {
    if (!newDomain.trim() || !registryId) return;
    addDomain.mutate({ id: registryId, domain: newDomain.trim() });
    setNewDomain('');
  };

  return (
    <div>
      <PageHeader
        title={registry.name}
        description={`Ecosystem: ${registry.ecosystem}`}
        actions={
          <button
            onClick={() => toggleRegistry.mutate({ id: registryId! })}
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              registry.isEnabled
                ? 'bg-green-ok/20 text-green-ok'
                : 'bg-surface-raised text-text-secondary'
            }`}
          >
            {registry.isEnabled ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <XCircle className="h-4 w-4" />
            )}
            {registry.isEnabled ? 'Enabled' : 'Disabled'}
          </button>
        }
      />

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Registry Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-text-secondary">Name</Label>
                {isEditing ? (
                  <div className="mt-1 flex items-center gap-2">
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="max-w-sm"
                    />
                    <Button size="sm" onClick={handleSaveName} disabled={updateRegistry.isPending}>
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="mt-1 flex items-center gap-2">
                    <p className="text-text-primary">{registry.name}</p>
                    <Button size="sm" variant="ghost" onClick={() => setIsEditing(true)}>
                      Edit
                    </Button>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-text-secondary">Ecosystem</Label>
                <p className="mt-1 text-text-primary">
                  <Badge variant="secondary">{registry.ecosystem}</Badge>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                Domains ({registry.domains.length})
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex items-center gap-2">
              <Input
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                placeholder="e.g., registry.npmjs.org"
                className="max-w-sm"
              />
              <Button
                size="sm"
                onClick={handleAddDomain}
                disabled={!newDomain.trim() || addDomain.isPending}
              >
                <Plus className="mr-1 h-4 w-4" />
                Add
              </Button>
            </div>

            {registry.domains.length === 0 ? (
              <p className="text-sm text-text-secondary">No domains configured</p>
            ) : (
              <div className="space-y-2">
                {registry.domains.map((domain) => (
                  <div
                    key={domain.id}
                    className="flex items-center justify-between rounded-md border border-border px-4 py-2"
                  >
                    <span className="text-sm text-text-primary">{domain.domain}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        removeDomain.mutate({ id: registryId!, domainId: domain.id })
                      }
                      disabled={removeDomain.isPending}
                      aria-label={`Remove domain ${domain.domain}`}
                    >
                      <Trash2 className="h-4 w-4 text-text-secondary hover:text-red-accent" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
